import React, { useState, useEffect, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import { reportsAPI, ttsAPI, settingsAPI } from '../services/api';
import PaymentModal from './PaymentModal';
import { useYear } from '../context/YearContext';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';

const fmt = (n) =>
  new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(n || 0);

const sum = (arr, key) => arr.reduce((s, r) => s + parseFloat(r[key] || 0), 0);

const waLink = (mobile, studentName, pendingAmount, schoolName) => {
  const msg =
    `${schoolName || 'గీతం స్కూల్'} నుండి నమస్కారం 🙏\n\nప్రియమైన తల్లిదండ్రులకు,\nమీ పిల్లల పేరు: *${studentName}*\nబాకీ మొత్తం: *${fmt(pendingAmount)}*\n\nమీ పిల్లల ఫీజు ఇంకా పెండింగ్‌లో ఉంది. దయచేసి వీలైనంత త్వరగా ఫీజును చెల్లించి సహకరించగలరు.\n\nమీ సహకారం మా విద్యార్థుల అభివృద్ధికి ఎంతో ముఖ్యమైనది. ఏవైనా సందేహాలు ఉంటే స్కూల్ కార్యాలయాన్ని సంప్రదించండి.\n\nధన్యవాదాలు,\n${schoolName || 'గీతం స్కూల్'}`;
  return `https://wa.me/91${mobile}?text=${encodeURIComponent(msg)}`;
};

const speakTelugu = (text) => {
  if (!window.speechSynthesis) {
    alert('మీ బ్రౌజర్ TTS కి సపోర్ట్ చేయడం లేదు. Chrome/Edge ఉపయోగించండి.');
    return;
  }
  window.speechSynthesis.cancel();
  const doSpeak = () => {
    const utter = new SpeechSynthesisUtterance(text);
    const voices = window.speechSynthesis.getVoices();
    const teVoice = voices.find((v) => v.lang.startsWith('te'));
    if (teVoice) utter.voice = teVoice;
    utter.lang  = 'te-IN';
    utter.rate  = 0.85;
    utter.pitch = 1;
    window.speechSynthesis.speak(utter);
  };
  if (window.speechSynthesis.getVoices().length) {
    doSpeak();
  } else {
    window.speechSynthesis.onvoiceschanged = doSpeak;
  }
};

const exportCSV = (rows, filename) => {
  const headers = ['#', 'Student Name', 'Father Name', 'Mobile', 'Class', 'Village', 'Pending Amount'];
  const lines = [
    headers.join(','),
    ...rows.map((s, i) =>
      [
        i + 1,
        `"${s.student_name}"`,
        `"${s.father_name || ''}"`,
        s.mobile_number || '',
        `"${s.class}"`,
        `"${s.village}"`,
        parseFloat(s.pending_amount || 0).toFixed(2),
      ].join(',')
    ),
  ];
  const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8;' });
  const url  = URL.createObjectURL(blob);
  const a    = document.createElement('a');
  a.href     = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
};

const exportCollBookCSV = (rows, groupBy) => {
  const headers = [groupBy === 'month' ? 'Month' : 'Date', 'Payments', 'Total Collected', 'Details'];
  const lines = [
    headers.join(','),
    ...rows.map((r) =>
      [
        r.period,
        r.payment_count,
        parseFloat(r.total_collected || 0).toFixed(2),
        `"${(r.payment_details || '').replace(/"/g, "'")}"`,
      ].join(',')
    ),
  ];
  const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8;' });
  const url  = URL.createObjectURL(blob);
  const a    = document.createElement('a');
  a.href     = url;
  a.download = `collection_book_${new Date().toISOString().split('T')[0]}.csv`;
  a.click();
  URL.revokeObjectURL(url);
};

export default function Reports({ schoolName }) {
  const { year }                          = useYear();
  const [activeTab,       setActiveTab]       = useState('village');
  const [villageData,     setVillageData]     = useState([]);
  const [classData,       setClassData]       = useState([]);
  const [pendingStudents, setPendingStudents] = useState([]);
  const [allStudents,     setAllStudents]     = useState([]);
  const [expandedVillage, setExpandedVillage] = useState(null);
  const [expandedClass,   setExpandedClass]   = useState(null);
  const [loading,         setLoading]         = useState(true);
  const [error,           setError]           = useState(null);
  const [filterVillage,   setFilterVillage]   = useState('');
  const [filterClass,     setFilterClass]     = useState('');
  const [searchQuery,     setSearchQuery]     = useState('');
  const [copyMsg,         setCopyMsg]         = useState('');
  const [showPayment,     setShowPayment]     = useState(false);
  const [payStudent,      setPayStudent]      = useState(null);
  const [showBatchWA,     setShowBatchWA]     = useState(false);
  // Monthly wise state
  const [monthlyPayments, setMonthlyPayments] = useState([]);
  const [monthlyLoading,  setMonthlyLoading]  = useState(false);
  const [monthlyMonths,   setMonthlyMonths]   = useState(11);
  const [monthlySearch,   setMonthlySearch]   = useState('');
  // Monthly tab: uses global academic year (change from Dashboard only)
  const monthlyYear = year;
  const [monthlyMonthIdx,       setMonthlyMonthIdx]       = useState(() => { const m = new Date().getMonth()+1; return m>=6 ? m-6 : m+6; });
  const [monthlyClassFilter,    setMonthlyClassFilter]    = useState('');
  const [monthlyShowDefaulters, setMonthlyShowDefaulters] = useState(false);
  const [monthlyExpandedClasses,setMonthlyExpandedClasses] = useState(new Set());
  const [monthlyHistoryStudent, setMonthlyHistoryStudent]  = useState(null);
  // Collection book state
  const [collBook,        setCollBook]        = useState([]);
  const [collLoading,     setCollLoading]     = useState(false);
  const [collGroupBy,     setCollGroupBy]     = useState('day');
  const [collFrom,        setCollFrom]        = useState('');
  const [collTo,          setCollTo]          = useState('');
  // Voice reminder state
  const [voiceStudent,    setVoiceStudent]    = useState(null); // student being reminded
  const [voiceLoading,    setVoiceLoading]    = useState(false);
  const [voiceError,      setVoiceError]      = useState(null);

  const refreshPending = () =>
    reportsAPI.getPendingStudents(year).then((r) => setPendingStudents(r.data.data)).catch(() => {});

  // Refresh allStudents so PaymentModal always gets fresh pending_amount after a payment
  const refreshAllStudents = () =>
    reportsAPI.getAllStudents(year).then((r) => setAllStudents(r.data.data)).catch(() => {});

  // Always fetch fresh student data before opening PaymentModal so edits (e.g. hostel fee) are reflected
  const openPayModal = async (s) => {
    try {
      const [pendingRes, allRes] = await Promise.all([
        reportsAPI.getPendingStudents(year),
        reportsAPI.getAllStudents(year),
      ]);
      const freshPending = pendingRes.data.data || [];
      const freshAll     = allRes.data.data     || [];
      setPendingStudents(freshPending);
      setAllStudents(freshAll);
      const fresh = freshAll.find((r) => r.student_id === s.student_id)
                 || freshPending.find((r) => r.student_id === s.student_id)
                 || s;
      setPayStudent(fresh);
    } catch {
      setPayStudent(s);
    }
    setShowPayment(true);
  };

  // ── Voice Reminder: generate TTS audio → share to WhatsApp ──────────────────
  const sendVoiceReminder = async (s) => {
    setVoiceStudent(s);
    setVoiceLoading(true);
    setVoiceError(null);

    const text =
      `గీతం స్కూల్ నుండి నమస్కారం.\n\n` +
      `ప్రియమైన తల్లిదండ్రులకు,\n` +
      `మీ పిల్లల పేరు ${s.student_name}. ` +
      `వారి ఫీజు బాకీ మొత్తం ${fmt(s.pending_amount)}. ` +
      `మీ పిల్లల ఫీజు ఇంకా పెండింగ్‌లో ఉంది. ` +
      `దయచేసి వీలైనంత త్వరగా ఫీజును చెల్లించి సహకరించగలరు.\n\n` +
      `మీ సహకారం మా విద్యార్థుల అభివృద్ధికి ఎంతో ముఖ్యమైనది. ` +
      `ఏవైనా సందేహాలు ఉంటే స్కూల్ కార్యాలయాన్ని సంప్రదించండి.\n\n` +
      `ధన్యవాదాలు, గీతం స్కూల్.`;

    try {
      const res = await ttsAPI.speak(text);
      const audioBlob = new Blob([res.data], { type: 'audio/mpeg' });
      const fileName  = `fee_reminder_${s.student_name.replace(/\s+/g, '_')}.mp3`;
      const audioFile = new File([audioBlob], fileName, { type: 'audio/mpeg' });

      // Try Web Share API (native share sheet — picks WhatsApp on mobile)
      if (navigator.canShare && navigator.canShare({ files: [audioFile] })) {
        await navigator.share({
          files: [audioFile],
          title: `Fee Reminder — ${schoolName || 'Geetham School'}`,
          text:  `Fee reminder for ${s.student_name}`,
        });
      } else {
        // Fallback: auto-download the audio
        const url = URL.createObjectURL(audioBlob);
        const a   = document.createElement('a');
        a.href     = url;
        a.download = fileName;
        a.click();
        URL.revokeObjectURL(url);
      }
    } catch (err) {
      if (err.name === 'AbortError') { /* user cancelled share */ }
      else setVoiceError(err.response?.data?.message || err.message || 'Failed to generate voice');
    } finally {
      setVoiceLoading(false);
    }
  };

  useEffect(() => {
    setLoading(true);
    Promise.all([
      reportsAPI.getVillageReport(year),
      reportsAPI.getClassReport(year),
      reportsAPI.getPendingStudents(year),
      reportsAPI.getAllStudents(year),
    ])
      .then(([vRes, cRes, pRes, aRes]) => {
        setVillageData(vRes.data.data);
        setClassData(cRes.data.data);
        setPendingStudents(pRes.data.data);
        setAllStudents(aRes.data.data);
      })
      .catch(() => setError('Failed to load reports'))
      .finally(() => setLoading(false));
  }, [year]);

  const fetchCollectionBook = useCallback(async () => {
    setCollLoading(true);
    try {
      const params = { year, group_by: collGroupBy };
      if (collFrom) params.from_date = collFrom;
      if (collTo)   params.to_date   = collTo;
      const res = await reportsAPI.getCollectionBook(params);
      setCollBook(res.data.data);
    } catch {
      setCollBook([]);
    } finally {
      setCollLoading(false);
    }
  }, [year, collGroupBy, collFrom, collTo]);

  const fetchMonthlyData = useCallback(async () => {
    setMonthlyLoading(true);
    try {
      const [mpRes, stRes] = await Promise.all([
        reportsAPI.getMonthlyPayments(monthlyYear),
        settingsAPI.getAll(),
      ]);
      setMonthlyPayments(mpRes.data.data || []);
      const mm = parseInt(stRes.data.data?.monthly_months, 10);
      if (!isNaN(mm) && mm >= 1 && mm <= 12) setMonthlyMonths(mm);
    } catch {
      setMonthlyPayments([]);
    } finally {
      setMonthlyLoading(false);
    }
  }, [monthlyYear]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (activeTab === 'collection') fetchCollectionBook();
    if (activeTab === 'monthly')    fetchMonthlyData();
  }, [activeTab, fetchCollectionBook, fetchMonthlyData]);

  const villages = [...new Set(pendingStudents.map((s) => s.village))].sort();
  const classes  = [...new Set(pendingStudents.map((s) => s.class))].sort();

  const filteredPending = pendingStudents.filter((s) => {
    if (filterVillage && s.village !== filterVillage) return false;
    if (filterClass   && s.class   !== filterClass)   return false;
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      const nameMatch   = s.student_name?.toLowerCase().includes(q);
      const mobileMatch = s.mobile_number?.includes(q);
      if (!nameMatch && !mobileMatch) return false;
    }
    return true;
  });

  if (loading) return (
    <div className="page">
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px,1fr))', gap: '.65rem', marginBottom: '1.25rem' }}>
        {[1,2,3,4].map((i) => (
          <div key={i} className="skeleton-card"><div className="skeleton skeleton-title" /><div className="skeleton skeleton-text" /></div>
        ))}
      </div>
      <div className="loading">Loading accounts…</div>
    </div>
  );
  if (error)   return <div className="error-page">{error}</div>;

  // ── Analytics summary values ──
  const totalVillages   = villageData.length;
  const totalClasses    = classData.length;
  const totalStudents   = classData.reduce((s, r) => s + r.total_students, 0);
  const totalCollected  = villageData.reduce((s, r) => s + parseFloat(r.total_collected || 0), 0);
  const totalPending    = villageData.reduce((s, r) => s + parseFloat(r.total_pending   || 0), 0);
  const totalFees       = villageData.reduce((s, r) => s + parseFloat(r.total_fees      || 0), 0);
  const collectionRate  = totalFees > 0 ? ((totalCollected / totalFees) * 100).toFixed(1) : 0;

  return (
    <div className="page">
      <h1 className="page-title">Accounts — {year}</h1>

      <div className="tabs">
        <button className={`tab ${activeTab === 'village' ? 'active' : ''}`} onClick={() => setActiveTab('village')}>
          🏘 Village-wise
        </button>
        <button className={`tab ${activeTab === 'class' ? 'active' : ''}`} onClick={() => setActiveTab('class')}>
          🏫 Class-wise
        </button>
        <button className={`tab ${activeTab === 'pending' ? 'active' : ''}`} onClick={() => setActiveTab('pending')}>
          ⏳ Due Students {pendingStudents.length > 0 && <span className="tab-badge">{pendingStudents.length}</span>}
        </button>
        <button className={`tab ${activeTab === 'collection' ? 'active' : ''}`} onClick={() => setActiveTab('collection')}>
          📊 Collection Book
        </button>
        <button className={`tab ${activeTab === 'monthly' ? 'active' : ''}`} onClick={() => setActiveTab('monthly')}>
          📅 Monthly Wise
        </button>
      </div>

      {activeTab === 'village' ? (
        <div className="table-container">
          <table className="data-table">
            <thead>
              <tr>
                <th>Village</th>
                <th>Students</th>
                <th>Total Fees</th>
                <th>Collected</th>
                <th>Pending</th>
                <th>Collection %</th>
              </tr>
            </thead>
            <tbody>
              {villageData.length === 0 ? (
                <tr><td colSpan="6" className="no-data-cell">No data available</td></tr>
              ) : (
                villageData.map((row) => {
                  const pct = row.total_fees > 0
                    ? ((row.total_collected / row.total_fees) * 100).toFixed(1)
                    : 0;
                  const isOpen = expandedVillage === row.village;
                  const rowStudents = allStudents.filter((s) => s.village === row.village);
                  return (
                    <React.Fragment key={row.village}>
                      <tr
                        className="expandable-row"
                        onClick={() => setExpandedVillage(isOpen ? null : row.village)}
                        title="Click to see students"
                      >
                        <td>
                          <span className="expand-arrow">{isOpen ? '▼' : '▶'}</span>
                          <strong>{row.village}</strong>
                        </td>
                        <td>{row.total_students}</td>
                        <td>{fmt(row.total_fees)}</td>
                        <td className="text-green">{fmt(row.total_collected)}</td>
                        <td className={parseFloat(row.total_pending) > 0 ? 'text-red' : 'text-green'}>
                          {fmt(row.total_pending)}
                        </td>
                        <td>
                          <div className="inline-progress">
                            <div className="inline-bar">
                              <div className="inline-fill" style={{ width: `${Math.min(pct, 100)}%` }} />
                            </div>
                            <span>{pct}%</span>
                          </div>
                        </td>
                      </tr>
                      {isOpen && rowStudents.map((s) => (
                        <tr key={s.student_id} className="student-sub-row">
                          <td colSpan="2" className="sub-name">
                            &nbsp;&nbsp;&nbsp;👤 {s.student_name}
                            {s.father_name && <span className="sub-father"> / {s.father_name}</span>}
                          </td>
                          <td className="sub-cell">Class {s.class}</td>
                          <td className="text-green sub-cell">{fmt(s.paid_amount)}</td>
                          <td className={parseFloat(s.pending_amount) > 0 ? 'text-red sub-cell' : 'text-green sub-cell'}>
                            {fmt(s.pending_amount)}
                          </td>
                          <td className="sub-cell">
                            {parseFloat(s.pending_amount) > 0 ? (
                              <a
                                href={waLink(s.mobile_number, s.student_name, s.pending_amount, schoolName)}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="btn btn-sm btn-whatsapp"
                                onClick={(e) => e.stopPropagation()}
                              >📲</a>
                            ) : (
                              <span className="badge-paid">✅ Paid</span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </React.Fragment>
                  );
                })
              )}
            </tbody>
            {villageData.length > 0 && (
              <tfoot>
                <tr>
                  <td><strong>Grand Total</strong></td>
                  <td><strong>{villageData.reduce((s, r) => s + r.total_students, 0)}</strong></td>
                  <td><strong>{fmt(sum(villageData, 'total_fees'))}</strong></td>
                  <td className="text-green"><strong>{fmt(sum(villageData, 'total_collected'))}</strong></td>
                  <td className="text-red"><strong>{fmt(sum(villageData, 'total_pending'))}</strong></td>
                  <td />
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      ) : activeTab === 'class' ? (
        <div className="table-container">
          <table className="data-table">
            <thead>
              <tr>
                <th>Class</th>
                <th>Total</th>
                <th>Paid ✅</th>
                <th>Pending ⏳</th>
                <th>Total Fees</th>
                <th>Collected</th>
                <th>Pending Amt</th>
              </tr>
            </thead>
            <tbody>
              {classData.length === 0 ? (
                <tr><td colSpan="7" className="no-data-cell">No data available</td></tr>
              ) : (
                classData.map((row) => {
                  const isOpen = expandedClass === row.class;
                  const rowStudents = allStudents.filter((s) => s.class === row.class);
                  return (
                    <React.Fragment key={row.class}>
                      <tr
                        className="expandable-row"
                        onClick={() => setExpandedClass(isOpen ? null : row.class)}
                        title="Click to see students"
                      >
                        <td>
                          <span className="expand-arrow">{isOpen ? '▼' : '▶'}</span>
                          <strong>{row.class}</strong>
                        </td>
                        <td>{row.total_students}</td>
                        <td className="text-green">{row.paid_students}</td>
                        <td className={row.pending_students > 0 ? 'text-red' : 'text-green'}>{row.pending_students}</td>
                        <td>{fmt(row.total_fees)}</td>
                        <td className="text-green">{fmt(row.total_collected)}</td>
                        <td className={parseFloat(row.total_pending) > 0 ? 'text-red' : 'text-green'}>
                          {fmt(row.total_pending)}
                        </td>
                      </tr>
                      {isOpen && rowStudents.map((s) => (
                        <tr key={s.student_id} className="student-sub-row">
                          <td colSpan="2" className="sub-name">
                            &nbsp;&nbsp;&nbsp;👤 {s.student_name}
                            {s.father_name && <span className="sub-father"> / {s.father_name}</span>}
                          </td>
                          <td className="sub-cell">{s.village}</td>
                          <td className={parseFloat(s.pending_amount) > 0 ? 'text-red sub-cell' : 'text-green sub-cell'}>
                            {parseFloat(s.pending_amount) > 0 ? '⏳' : '✅'}
                          </td>
                          <td className="sub-cell">{fmt(s.total_fee)}</td>
                          <td className="text-green sub-cell">{fmt(s.paid_amount)}</td>
                          <td className={parseFloat(s.pending_amount) > 0 ? 'text-red sub-cell' : 'text-green sub-cell'}>
                            {fmt(s.pending_amount)}
                          </td>
                        </tr>
                      ))}
                    </React.Fragment>
                  );
                })
              )}
            </tbody>
            {classData.length > 0 && (
              <tfoot>
                <tr>
                  <td><strong>Grand Total</strong></td>
                  <td><strong>{classData.reduce((s, r) => s + r.total_students, 0)}</strong></td>
                  <td className="text-green"><strong>{classData.reduce((s, r) => s + r.paid_students, 0)}</strong></td>
                  <td className="text-red"><strong>{classData.reduce((s, r) => s + r.pending_students, 0)}</strong></td>
                  <td><strong>{fmt(sum(classData, 'total_fees'))}</strong></td>
                  <td className="text-green"><strong>{fmt(sum(classData, 'total_collected'))}</strong></td>
                  <td className="text-red"><strong>{fmt(sum(classData, 'total_pending'))}</strong></td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      ) : activeTab === 'pending' ? (
        /* ── Due Students tab ── */
        <>
          <div className="filter-bar">
            <div className="search-box-wrap">
              <span className="search-icon">🔍</span>
              <input
                type="text"
                className="search-input"
                placeholder="Search by name or mobile…"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
              {searchQuery && (
                <button className="search-clear" onClick={() => setSearchQuery('')} title="Clear">×</button>
              )}
            </div>
            <select
              value={filterVillage}
              onChange={(e) => setFilterVillage(e.target.value)}
              className="filter-select"
            >
              <option value="">All Villages</option>
              {villages.map((v) => <option key={v} value={v}>{v}</option>)}
            </select>
            <select
              value={filterClass}
              onChange={(e) => setFilterClass(e.target.value)}
              className="filter-select"
            >
              <option value="">All Classes</option>
              {classes.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
            {(filterVillage || filterClass || searchQuery) && (
              <button className="btn btn-secondary" onClick={() => { setFilterVillage(''); setFilterClass(''); setSearchQuery(''); }}>
                ✕ Clear
              </button>
            )}
            <div style={{ flex: 1 }} />
            {filteredPending.length > 0 && (
              <>
                <button
                  className="btn btn-secondary"
                  title="Export due students list as CSV"
                  onClick={() => exportCSV(filteredPending, `due_students_${new Date().toISOString().split('T')[0]}.csv`)}
                >
                  📊 Export CSV
                </button>
                <button
                  className="btn btn-secondary"
                  title="Copy all mobile numbers to clipboard"
                  onClick={() => {
                    const nums = filteredPending
                      .filter((s) => s.mobile_number)
                      .map((s) => s.mobile_number)
                      .join(', ');
                    navigator.clipboard.writeText(nums).then(() => {
                      setCopyMsg('✓ Copied!');
                      setTimeout(() => setCopyMsg(''), 2500);
                    });
                  }}
                >
                  📱 Copy Numbers {copyMsg && <span style={{ color: '#22c55e', marginLeft: 4 }}>{copyMsg}</span>}
                </button>
                <button
                  className="btn btn-primary"
                  title="Open a pre-filled bulk WhatsApp reminder message in Telugu"
                  onClick={() => {
                    const lines = filteredPending
                      .map((s) => `• ${s.student_name} (${s.class}) – ${fmt(s.pending_amount)}`)
                      .join('\n');
                    const msg =
                      `గీతం స్కూల్ నుండి నమస్కారం 🙏\n\n` +
                      `ప్రియమైన తల్లిదండ్రులకు,\n` +
                      `క్రింది విద్యార్థుల ఫీజు ఇంకా పెండింగ్‌లో ఉంది:\n\n${lines}\n\n` +
                      `మీ పిల్లల ఫీజు ఇంకా పెండింగ్‌లో ఉంది. దయచేసి వీలైనంత త్వరగా ఫీజును చెల్లించి సహకరించగలరు.\n\n` +
                      `మీ సహకారం మా విద్యార్థుల అభివృద్ధికి ఎంతో ముఖ్యమైనది.\n\n` +
                      `ధన్యవాదాలు,\nగీతం స్కూల్`;
                    window.open(`https://wa.me/?text=${encodeURIComponent(msg)}`, '_blank', 'noopener,noreferrer');
                  }}
                >
                  📢 Bulk WhatsApp
                </button>
                <button
                  className="btn btn-primary"
                  title="Send personalised WhatsApp to each student one by one"
                  onClick={() => setShowBatchWA(true)}
                >
                  🚀 Send to Each Student
                </button>
              </>
            )}
          </div>
          <div className="table-container">
            <table className="data-table">
              <thead>
                <tr>
                  <th>#</th>
                  <th>Student Name</th>
                  <th>Father Name</th>
                  <th>Mobile</th>
                  <th>Class</th>
                  <th>Village</th>
                  <th>Pending Amount</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {filteredPending.length === 0 ? (
                  <tr><td colSpan="8" className="no-data-cell">🎉 No pending dues!</td></tr>
                ) : (
                  filteredPending.map((s, i) => (
                    <tr key={s.student_id}>
                      <td className="id-cell">{i + 1}</td>
                      <td><strong>{s.student_name}</strong></td>
                      <td>{s.father_name || '—'}</td>
                      <td>
                        {s.mobile_number ? (
                          <a
                            href={waLink(s.mobile_number, s.student_name, s.pending_amount, schoolName)}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="wa-link"
                            title="Send WhatsApp reminder"
                          >
                            📱 {s.mobile_number}
                          </a>
                        ) : '—'}
                      </td>
                      <td><span className="badge">{s.class}</span></td>
                      <td>{s.village}</td>
                      <td>
                        <span className="pending-chip due">{fmt(s.pending_amount)}</span>
                      </td>
                      <td>
                        <button
                          className="btn btn-sm btn-success"
                          title="Record Payment"
                          onClick={() => openPayModal(s)}
                        >
                          💳 Pay
                        </button>
                        {s.mobile_number && (
                          <button
                            className="btn btn-sm btn-whatsapp"
                            title="తెలుగు వాయిస్ రిమాఇండర్ WhatsApp లో పంపండి"
                            style={{ marginLeft: 4 }}
                            disabled={voiceLoading && voiceStudent?.student_id === s.student_id}
                            onClick={() => sendVoiceReminder(s)}
                          >
                            {voiceLoading && voiceStudent?.student_id === s.student_id
                              ? '⏳…'
                              : '🎙️ Voice'}
                          </button>
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
              {filteredPending.length > 0 && (
                <tfoot>
                  <tr>
                    <td colSpan="6"><strong>Total Due ({filteredPending.length} students)</strong></td>
                    <td className="text-red"><strong>{fmt(sum(filteredPending, 'pending_amount'))}</strong></td>
                    <td />
                  </tr>
                </tfoot>
              )}
            </table>
          </div>
        </>
      ) : null}

      {/* ── Collection Book tab ── */}
      {activeTab === 'collection' && (
        <>
          <div className="filter-bar" style={{ marginTop: 16 }}>
            <select
              value={collGroupBy}
              onChange={(e) => setCollGroupBy(e.target.value)}
              className="filter-select"
            >
              <option value="day">Daily View</option>
              <option value="month">Monthly View</option>
            </select>
            <input
              type="date"
              className="filter-select"
              value={collFrom}
              onChange={(e) => setCollFrom(e.target.value)}
              placeholder="From date"
              lang="en-GB"
            />
            <input
              type="date"
              className="filter-select"
              value={collTo}
              onChange={(e) => setCollTo(e.target.value)}
              placeholder="To date"
              lang="en-GB"
            />
            <button className="btn btn-primary" onClick={fetchCollectionBook}>
              🔍 Load
            </button>
            {collBook.length > 0 && (
              <button
                className="btn btn-secondary"
                title="Export collection book as CSV"
                onClick={() => exportCollBookCSV(collBook, collGroupBy)}
              >
                📊 Export CSV
              </button>
            )}
            {(collFrom || collTo) && (
              <button className="btn btn-secondary" onClick={() => { setCollFrom(''); setCollTo(''); }}>
                ✕ Clear
              </button>
            )}
          </div>
          {collLoading ? (
            <div className="loading">Loading collection book…</div>
          ) : (
            <>
              {/* ── Mini bar chart ── */}
              {collBook.length > 1 && (
                <div className="card" style={{ marginBottom: '1rem', padding: '1rem 1.25rem' }}>
                  <div style={{ fontSize: '.8rem', fontWeight: 700, color: 'var(--slate-600)', marginBottom: '.75rem', textTransform: 'uppercase', letterSpacing: '.5px' }}>
                    Collection Trend
                  </div>
                  <ResponsiveContainer width="100%" height={130}>
                    <BarChart data={collBook.slice(-12)} margin={{ top: 0, right: 0, left: 0, bottom: 0 }} barSize={18}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
                      <XAxis dataKey="period" tick={{ fill: '#94a3b8', fontSize: 9 }} axisLine={false} tickLine={false} />
                      <YAxis hide />
                      <Tooltip
                        content={({ active, payload, label }) =>
                          active && payload?.length ? (
                            <div style={{ background: '#1e293b', borderRadius: 8, padding: '6px 12px' }}>
                              <p style={{ color: '#94a3b8', fontSize: 10, margin: 0 }}>{label}</p>
                              <p style={{ color: '#22c55e', fontSize: 12, fontWeight: 700, margin: '2px 0 0' }}>
                                {new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(payload[0].value)}
                              </p>
                            </div>
                          ) : null
                        }
                      />
                      <Bar dataKey="total_collected" name="Collected" fill="#6366f1" radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              )}
            <div className="table-container">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>{collGroupBy === 'month' ? 'Month' : 'Date'}</th>
                    <th>Payments</th>
                    <th>Total Collected</th>
                    <th>Details</th>
                  </tr>
                </thead>
                <tbody>
                  {collBook.length === 0 ? (
                    <tr><td colSpan="4">
                      <div className="empty-state">
                        <div className="empty-state-icon">📊</div>
                        <div className="empty-state-title">No records found</div>
                        <div className="empty-state-sub">Select a date range and click Load to view collection data.</div>
                      </div>
                    </td></tr>
                  ) : (
                    collBook.map((row) => (
                      <tr key={row.period}>
                        <td><strong>{row.period}</strong></td>
                        <td>{row.payment_count}</td>
                        <td className="text-green">{fmt(row.total_collected)}</td>
                        <td style={{ fontSize: '0.82rem', color: '#64748b', maxWidth: 320, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {row.payment_details || '—'}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
                {collBook.length > 0 && (
                  <tfoot>
                    <tr>
                      <td><strong>Grand Total</strong></td>
                      <td><strong>{collBook.reduce((s, r) => s + r.payment_count, 0)}</strong></td>
                      <td className="text-green"><strong>{fmt(sum(collBook, 'total_collected'))}</strong></td>
                      <td />
                    </tr>
                  </tfoot>
                )}
              </table>
            </div>
            </>
          )}
        </>
      )}

      {/* ── Monthly Wise tab — class accordion design ── */}
      {activeTab === 'monthly' && (() => {
        const MONTHS_SHORT = ['Jun','Jul','Aug','Sep','Oct','Nov','Dec','Jan','Feb','Mar','Apr','May'];
        const MONTH_NUM    = {Jan:1,Feb:2,Mar:3,Apr:4,May:5,Jun:6,Jul:7,Aug:8,Sep:9,Oct:10,Nov:11,Dec:12};
        const yearMatch    = String(monthlyYear||'').match(/^(\d{4})/);
        const startYear    = yearMatch ? parseInt(yearMatch[1],10) : new Date().getFullYear();
        const today        = new Date(); today.setHours(0,0,0,0);
        const selIdx       = Math.max(0, Math.min(11, monthlyMonthIdx));

        // Month metadata array
        const monthMeta = MONTHS_SHORT.map((mon, idx) => {
          const yr = idx <= 6 ? startYear : startYear + 1;
          return { mon, idx, yr, key:`${mon.toLowerCase()} ${yr}`, label:`${mon} ${yr}`,
            date: new Date(yr, MONTH_NUM[mon]-1, 1), isPast: new Date(yr, MONTH_NUM[mon]-1, 1) <= today };
        });
        const selMeta = monthMeta[selIdx];

        // Per-student payment map
        const paidMapByStudent = {};
        monthlyPayments.forEach((p) => {
          if (!paidMapByStudent[p.student_id]) paidMapByStudent[p.student_id] = {};
          paidMapByStudent[p.student_id][(p.month_year||'').toLowerCase()] = parseFloat(p.paid_amount||0);
        });
        const getPaid = (sid, meta) => Object.entries(paidMapByStudent[sid]||{}).reduce((s,[k,v]) =>
          k.includes(meta.mon.toLowerCase()) && k.includes(String(meta.yr)) ? s+v : s, 0);
        const getStatus = (sid, inst, meta) => {
          const paid = getPaid(sid, meta);
          if (!meta.isPast && paid===0) return 'upcoming';
          if (paid===0) return 'overdue';
          if (paid < inst) return 'partial';
          return 'paid';
        };

        // Group students by class
        const activeStudents = allStudents.filter(s => parseFloat(s.total_fee||0) > 0);
        const classGroups = {};
        activeStudents.forEach(s => { const c = s.class||'N/A'; if (!classGroups[c]) classGroups[c]=[]; classGroups[c].push(s); });
        const sortedClasses = Object.keys(classGroups).sort((a,b) => { const na=parseInt(a),nb=parseInt(b); return (!isNaN(na)&&!isNaN(nb)) ? na-nb : a.localeCompare(b); });
        const visClasses = monthlyClassFilter ? sortedClasses.filter(c=>c===monthlyClassFilter) : sortedClasses;

        // Summary for selected month
        let totalSt=0, paidCt=0, overdueCt=0, amtCollected=0, amtDue=0;
        visClasses.forEach(cls => classGroups[cls].forEach(s => {
          const inst=Math.round(parseFloat(s.total_fee||0)/monthlyMonths), st=getStatus(s.student_id,inst,selMeta), paid=getPaid(s.student_id,selMeta);
          totalSt++; amtCollected+=paid;
          if (st==='paid') paidCt++; else if (st==='overdue'||st==='partial') { overdueCt++; amtDue+=inst-paid; } else if (st==='upcoming') { amtDue+=inst-paid; }
        }));
        const stOrder = {overdue:0,partial:1,paid:2,upcoming:3};


        return (
          <>
            {/* Controls */}
            <div style={{marginTop:14,display:'flex',flexWrap:'wrap',alignItems:'center',gap:8,padding:'10px 12px',background:'#fff',borderRadius:10,border:'1px solid #e2e8f0',marginBottom:12}}>
              {/* Month Navigator */}
              <div style={{display:'flex',alignItems:'center',gap:6,background:'#eef2ff',borderRadius:10,padding:'5px 10px',border:'1px solid #c7d2fe',flexShrink:0}}>
                <button onClick={() => setMonthlyMonthIdx(Math.max(0,selIdx-1))} disabled={selIdx===0}
                  style={{width:26,height:26,borderRadius:7,border:'none',background:'#fff',color:'#4f46e5',fontWeight:'bold',fontSize:'1rem',cursor:selIdx===0?'default':'pointer',opacity:selIdx===0?0.3:1}}>‹</button>
                <div style={{textAlign:'center',minWidth:84}}>
                  <div style={{fontWeight:700,color:'#4f46e5',fontSize:'.9rem'}}>{selMeta.label}</div>
                </div>
                <button onClick={() => setMonthlyMonthIdx(Math.min(11,selIdx+1))} disabled={selIdx===11}
                  style={{width:26,height:26,borderRadius:7,border:'none',background:'#fff',color:'#4f46e5',fontWeight:'bold',fontSize:'1rem',cursor:selIdx===11?'default':'pointer',opacity:selIdx===11?0.3:1}}>›</button>
              </div>
              {/* Search */}
              <div style={{position:'relative',flex:'1 1 130px',minWidth:120}}>
                <span style={{position:'absolute',left:8,top:'50%',transform:'translateY(-50%)',color:'#94a3b8',fontSize:'.8rem',pointerEvents:'none'}}>🔍</span>
                <input type="text" placeholder="Search name / mobile..." value={monthlySearch}
                  onChange={e=>setMonthlySearch(e.target.value)}
                  style={{width:'100%',paddingLeft:26,paddingRight:8,paddingTop:6,paddingBottom:6,border:'1px solid #e2e8f0',borderRadius:8,fontSize:'.84rem',outline:'none',boxSizing:'border-box'}}/>
              </div>
              {/* Class filter */}
              <select value={monthlyClassFilter} onChange={e=>setMonthlyClassFilter(e.target.value)}
                style={{padding:'6px 8px',borderRadius:8,border:'1px solid #e2e8f0',fontSize:'.84rem',background:'#fff',color:'#334155',fontWeight:600,cursor:'pointer',flexShrink:0}}>
                <option value="">All Classes</option>
                {sortedClasses.map(c=><option key={c} value={c}>Class {c}</option>)}
              </select>
              {/* Year display — change from Dashboard only */}
              <span style={{padding:'6px 8px',borderRadius:8,border:'1px solid #e2e8f0',fontSize:'.84rem',background:'#f9fafb',color:'#334155',fontWeight:600,flexShrink:0}}>
                {monthlyYear}
              </span>
              {/* Defaulters toggle */}
              <button onClick={()=>setMonthlyShowDefaulters(v=>!v)}
                style={{padding:'6px 12px',border:'none',borderRadius:8,fontWeight:700,fontSize:'.8rem',cursor:'pointer',flexShrink:0,
                  background:monthlyShowDefaulters?'#ef4444':'#f1f5f9',color:monthlyShowDefaulters?'#fff':'#64748b'}}>
                {monthlyShowDefaulters?'Defaulters Only (ON)':'Defaulters Only'}
              </button>
              <button onClick={fetchMonthlyData} style={{padding:'6px 10px',border:'1px solid #e2e8f0',borderRadius:8,fontSize:'.8rem',background:'#fff',cursor:'pointer',marginLeft:'auto',flexShrink:0}}>🔄 Refresh</button>
            </div>

            {monthlyLoading ? <div className="loading">Loading monthly data...</div> : (
              <>
                {/* Summary Cards */}
                <div style={{display:'grid',gridTemplateColumns:'repeat(4,1fr)',gap:10,marginBottom:14}}>
                  <div style={{background:'#fff',border:'1px solid #e2e8f0',borderRadius:12,padding:'11px 14px'}}>
                    <div style={{fontSize:'.7rem',color:'#94a3b8',fontWeight:700,marginBottom:3}}>STUDENTS</div>
                    <div style={{fontSize:'1.6rem',fontWeight:800,color:'#1e293b'}}>{totalSt}</div>
                    <div style={{fontSize:'.7rem',color:'#94a3b8'}}>{visClasses.length} class{visClasses.length!==1?'es':''}</div>
                  </div>
                  <div style={{background:'#f0fdf4',border:'1px solid #bbf7d0',borderRadius:12,padding:'11px 14px'}}>
                    <div style={{fontSize:'.7rem',color:'#16a34a',fontWeight:700,marginBottom:3}}>PAID</div>
                    <div style={{display:'flex',alignItems:'baseline',gap:6}}>
                      <span style={{fontSize:'1.6rem',fontWeight:800,color:'#16a34a'}}>{paidCt}</span>
                      <span style={{fontSize:'.8rem',color:'#86efac'}}>{totalSt?Math.round(paidCt/totalSt*100):0}%</span>
                    </div>
                    <div style={{marginTop:4,height:4,background:'#dcfce7',borderRadius:9999,overflow:'hidden'}}>
                      <div style={{height:'100%',background:'#22c55e',width:`${totalSt?paidCt/totalSt*100:0}%`,borderRadius:9999}}></div>
                    </div>
                  </div>
                  <div style={{background:'#fef2f2',border:'1px solid #fecaca',borderRadius:12,padding:'11px 14px'}}>
                    <div style={{fontSize:'.7rem',color:'#dc2626',fontWeight:700,marginBottom:3}}>DEFAULTERS</div>
                    <div style={{fontSize:'1.6rem',fontWeight:800,color:'#dc2626'}}>{overdueCt}</div>
                  </div>
                  <div style={{background:'#fffbeb',border:'1px solid #fde68a',borderRadius:12,padding:'11px 14px'}}>
                    <div style={{fontSize:'.7rem',color:'#b45309',fontWeight:700,marginBottom:3}}>AMOUNT DUE</div>
                    <div style={{fontSize:'1.1rem',fontWeight:800,color:'#b45309'}}>{fmt(amtDue)}</div>
                    <div style={{fontSize:'.7rem',color:'#94a3b8',marginTop:2}}>Collected: {fmt(amtCollected)}</div>
                  </div>
                </div>

                {/* Class Accordion */}
                <div style={{display:'flex',flexDirection:'column',gap:10}}>
                  {visClasses.map(cls => {
                    const students = classGroups[cls];
                    const isExpanded = monthlyExpandedClasses.has(cls);
                    let clsPaid=0,clsPartial=0,clsOverdue=0,clsUpcoming=0,clsAmtC=0,clsAmtD=0;
                    students.forEach(s => {
                      const inst=Math.round(parseFloat(s.total_fee||0)/monthlyMonths);
                      const st=getStatus(s.student_id,inst,selMeta);
                      const paid=getPaid(s.student_id,selMeta);
                      clsAmtC+=paid;
                      if(st==='paid')clsPaid++;
                      else if(st==='partial'){clsPartial++;clsAmtD+=inst-paid;}
                      else if(st==='overdue'){clsOverdue++;clsAmtD+=inst-paid;}
                      else if(st==='upcoming'){clsUpcoming++;clsAmtD+=inst-paid;}
                    });
                    const total=students.length;
                    const q=monthlySearch.toLowerCase();
                    const filtered=students.filter(s=>{
                      const ok=!q||s.student_name.toLowerCase().includes(q)||(s.mobile_number||'').includes(q);
                      if(!ok)return false;
                      if(monthlyShowDefaulters){const inst=Math.round(parseFloat(s.total_fee||0)/monthlyMonths);const st=getStatus(s.student_id,inst,selMeta);return st==='overdue'||st==='partial';}
                      return true;
                    });
                    if(monthlyShowDefaulters&&clsOverdue===0&&clsPartial===0)return null;
                    if(q&&filtered.length===0)return null;
                    const borderColor=clsOverdue>0?'#f87171':clsPartial>0?'#fbbf24':clsUpcoming>0?'#93c5fd':'#4ade80';
                    const iconBg=clsOverdue>0?'#fee2e2':clsPartial>0?'#fef3c7':clsUpcoming>0?'#eff6ff':'#dcfce7';
                    const iconColor=clsOverdue>0?'#dc2626':clsPartial>0?'#b45309':clsUpcoming>0?'#3b82f6':'#16a34a';
                    const paidPct=total?clsPaid/total*100:0;
                    const partPct=total?clsPartial/total*100:0;
                    const ovPct=total?clsOverdue/total*100:0;
                    const sorted=[...filtered].sort((a,b)=>{
                      const ia=Math.round(parseFloat(a.total_fee||0)/monthlyMonths);
                      const ib=Math.round(parseFloat(b.total_fee||0)/monthlyMonths);
                      return (stOrder[getStatus(a.student_id,ia,selMeta)]||0)-(stOrder[getStatus(b.student_id,ib,selMeta)]||0);
                    });
                    return (
                      <div key={cls} style={{background:'#fff',border:'1px solid #e2e8f0',borderLeft:`4px solid ${borderColor}`,borderRadius:12,overflow:'hidden'}}>
                        <div onClick={()=>{const nx=new Set(monthlyExpandedClasses);if(nx.has(cls))nx.delete(cls);else nx.add(cls);setMonthlyExpandedClasses(nx);}}
                          style={{padding:'11px 14px',cursor:'pointer',display:'flex',alignItems:'center',gap:10}}>
                          <div style={{width:42,height:42,borderRadius:9,background:iconBg,color:iconColor,display:'flex',alignItems:'center',justifyContent:'center',fontWeight:800,fontSize:'.75rem',flexShrink:0}}>Cl.{cls}</div>
                          <div style={{flex:1,minWidth:0}}>
                            <div style={{display:'flex',alignItems:'center',gap:6,flexWrap:'wrap',marginBottom:4}}>
                              <span style={{fontWeight:700,color:'#1e293b'}}>Class {cls}</span>
                              <span style={{fontSize:'.74rem',color:'#94a3b8'}}>{total} students</span>
                              {clsOverdue>0&&<span style={{fontSize:'.7rem',fontWeight:700,color:'#dc2626',background:'#fee2e2',padding:'1px 6px',borderRadius:5}}>{clsOverdue} overdue</span>}
                              {clsPartial>0&&<span style={{fontSize:'.7rem',fontWeight:700,color:'#b45309',background:'#fef3c7',padding:'1px 6px',borderRadius:5}}>{clsPartial} partial</span>}
                            </div>
                            <div style={{display:'flex',alignItems:'center',gap:8}}>
                              <div style={{flex:1,height:5,background:'#f1f5f9',borderRadius:9999,overflow:'hidden',display:'flex'}}>
                                <div style={{height:'100%',background:'#22c55e',width:`${paidPct}%`}}></div>
                                <div style={{height:'100%',background:'#fbbf24',width:`${partPct}%`}}></div>
                                <div style={{height:'100%',background:'#f87171',width:`${ovPct}%`}}></div>
                              </div>
                              <span style={{fontSize:'.77rem',fontWeight:700,color:clsPaid===total?'#16a34a':clsOverdue>0?'#dc2626':'#64748b',flexShrink:0}}>{clsPaid}/{total} paid</span>
                            </div>
                          </div>
                          <div style={{textAlign:'right',flexShrink:0}}>
                            <div style={{fontSize:'.85rem',fontWeight:700,color:'#16a34a'}}>{fmt(clsAmtC)}</div>
                            {clsAmtD>0?<div style={{fontSize:'.73rem',color:'#dc2626'}}>{fmt(clsAmtD)} due</div>:<div style={{fontSize:'.73rem',color:'#16a34a'}}>All clear</div>}
                          </div>
                          <span style={{color:'#94a3b8',fontSize:'1rem',flexShrink:0,display:'inline-block',transform:isExpanded?'rotate(180deg)':'rotate(0deg)',transition:'transform .2s'}}>▾</span>
                        </div>
                        {isExpanded && (
                          <div style={{borderTop:'1px solid #f1f5f9',padding:'4px 10px 8px'}}>
                            {sorted.length===0
                              ? <div style={{textAlign:'center',color:'#94a3b8',padding:14,fontSize:'.84rem'}}>No students match filter</div>
                              : sorted.map((s,i)=>{
                                const inst=Math.round(parseFloat(s.total_fee||0)/monthlyMonths);
                                const st=getStatus(s.student_id,inst,selMeta);
                                const paid=getPaid(s.student_id,selMeta);
                                const due=inst-paid;
                                const badge={
                                  paid:{bg:'#dcfce7',color:'#16a34a',lbl:'Paid'},
                                  partial:{bg:'#fef3c7',color:'#b45309',lbl:`Partial`},
                                  overdue:{bg:'#fee2e2',color:'#dc2626',lbl:'Overdue'},
                                  upcoming:{bg:'#f1f5f9',color:'#94a3b8',lbl:'Upcoming'}
                                }[st]||{bg:'#f1f5f9',color:'#94a3b8',lbl:'-'};
                                return (
                                  <div key={s.student_id} style={{display:'flex',alignItems:'center',gap:8,padding:'7px 2px',borderBottom:'1px solid #f8fafc'}}>
                                    <span style={{width:18,fontSize:'.7rem',color:'#cbd5e1',textAlign:'center',flexShrink:0}}>{i+1}</span>
                                    <div style={{flex:1,minWidth:0}}>
                                      <button onClick={()=>setMonthlyHistoryStudent({...s,instalment:inst})}
                                        style={{fontWeight:600,fontSize:'.84rem',color:'#1e293b',background:'none',border:'none',padding:0,cursor:'pointer',textAlign:'left'}}>
                                        {s.student_name}
                                      </button>
                                      <div style={{fontSize:'.71rem',color:'#94a3b8'}}>{s.mobile_number}</div>
                                    </div>
                                    <span style={{background:badge.bg,color:badge.color,borderRadius:6,padding:'2px 7px',fontSize:'.71rem',fontWeight:700,whiteSpace:'nowrap'}}>{badge.lbl}</span>
                                    <div style={{textAlign:'right',flexShrink:0,minWidth:60}}>
                                      {st==='paid'
                                        ?<span style={{fontSize:'.77rem',fontWeight:700,color:'#16a34a'}}>{fmt(paid)}</span>
                                        :<span style={{fontSize:'.77rem',fontWeight:700,color:'#dc2626'}}>{fmt(due)} due</span>}
                                    </div>
                                    {(st==='overdue'||st==='partial')
                                      ?<button className="btn btn-primary btn-sm" style={{flexShrink:0,padding:'3px 10px'}} onClick={()=>openPayModal(s)}>Pay</button>
                                      :<div style={{width:46,flexShrink:0}}></div>}
                                  </div>
                                );
                              })}
                          </div>
                        )}
                      </div>
                    );
                  }).filter(Boolean)}
                  {totalSt===0&&<div style={{textAlign:'center',padding:40,color:'#94a3b8',background:'#fff',borderRadius:12,border:'1px solid #e2e8f0'}}>No students found</div>}
                  {monthlyShowDefaulters&&overdueCt===0&&totalSt>0&&(
                    <div style={{textAlign:'center',padding:32,color:'#16a34a',background:'#f0fdf4',borderRadius:12,border:'1px solid #bbf7d0',fontWeight:700}}>All students paid for {selMeta.label}!</div>
                  )}
                </div>

                {/* Student 12-month history modal */}
                {monthlyHistoryStudent&&(()=>{
                  const hs=monthlyHistoryStudent;
                  let totalPaid=0,paidM=0,overdueM=0;
                  const cells=monthMeta.map(m=>{
                    const paid=getPaid(hs.student_id,m); totalPaid+=paid;
                    const st=(()=>{
                      if(!m.isPast&&paid===0)return 'upcoming';
                      if(paid===0)return 'overdue';
                      if(paid<hs.instalment)return 'partial';
                      return 'paid';
                    })();
                    if(st==='paid')paidM++; if(st==='overdue')overdueM++;
                    return{...m,st,paid};
                  });
                  const pal={
                    paid:{bg:'#dcfce7',bc:'#86efac',color:'#16a34a',icon:'OK'},
                    partial:{bg:'#fef3c7',bc:'#fde68a',color:'#b45309',icon:'~'},
                    overdue:{bg:'#fee2e2',bc:'#fca5a5',color:'#dc2626',icon:'X'},
                    upcoming:{bg:'#f1f5f9',bc:'#cbd5e1',color:'#94a3b8',icon:'-'}
                  };
                  return (
                    <div style={{position:'fixed',inset:0,background:'rgba(0,0,0,.45)',zIndex:1000,display:'flex',alignItems:'center',justifyContent:'center',padding:16}} onClick={()=>setMonthlyHistoryStudent(null)}>
                      <div style={{background:'#fff',borderRadius:16,padding:20,width:'100%',maxWidth:460,maxHeight:'90vh',overflowY:'auto',boxShadow:'0 20px 60px rgba(0,0,0,.25)'}} onClick={e=>e.stopPropagation()}>
                        <div style={{display:'flex',alignItems:'flex-start',justifyContent:'space-between',marginBottom:14}}>
                          <div>
                            <div style={{fontWeight:800,fontSize:'1rem',color:'#1e293b'}}>{hs.student_name}</div>
                            <div style={{fontSize:'.77rem',color:'#94a3b8',marginTop:2}}>Class {hs.class} · {fmt(hs.instalment)}/mo · {hs.mobile_number}</div>
                          </div>
                          <button onClick={()=>setMonthlyHistoryStudent(null)} style={{width:26,height:26,borderRadius:'50%',border:'none',background:'#f1f5f9',cursor:'pointer',fontSize:'1rem',color:'#64748b'}}>x</button>
                        </div>
                        <div style={{fontSize:'.68rem',fontWeight:700,color:'#94a3b8',textTransform:'uppercase',letterSpacing:'.05em',marginBottom:7}}>12-Month Payment History</div>
                        <div style={{display:'grid',gridTemplateColumns:'repeat(4,1fr)',gap:5,marginBottom:10}}>
                          {cells.map(c=>{
                            const p=pal[c.st]||pal.upcoming;
                            const ring=c.idx===selIdx?'0 0 0 2px #4f46e5,0 0 0 4px #fff':'none';
                            return (
                              <div key={c.key} style={{background:p.bg,border:`1px solid ${p.bc}`,borderRadius:9,padding:'5px 3px',textAlign:'center',boxShadow:ring}}>
                                <div style={{fontSize:'.68rem',fontWeight:700}}>{c.mon}</div>
                                <div style={{fontSize:'.9rem',fontWeight:800,color:p.color}}>{p.icon}</div>
                                {c.st==='partial'&&<div style={{fontSize:'.62rem',color:p.color}}>{fmt(c.paid)}</div>}
                              </div>
                            );
                          })}
                        </div>
                        <div style={{display:'grid',gridTemplateColumns:'repeat(3,1fr)',gap:8,background:'#f8fafc',borderRadius:10,padding:11,marginBottom:14,textAlign:'center'}}>
                          <div><div style={{fontWeight:800,fontSize:'1.05rem',color:'#16a34a'}}>{paidM}/12</div><div style={{fontSize:'.7rem',color:'#64748b'}}>Paid</div></div>
                          <div><div style={{fontWeight:800,fontSize:'1.05rem',color:'#dc2626'}}>{overdueM}</div><div style={{fontSize:'.7rem',color:'#64748b'}}>Overdue</div></div>
                          <div><div style={{fontWeight:800,fontSize:'.95rem',color:'#4f46e5'}}>{fmt(totalPaid)}</div><div style={{fontSize:'.7rem',color:'#64748b'}}>Collected</div></div>
                        </div>
                        <div style={{display:'flex',gap:8}}>
                          <button className="btn btn-primary" style={{flex:1}} onClick={()=>{setMonthlyHistoryStudent(null);openPayModal(hs);}}>Pay Now</button>
                          <button className="btn btn-secondary" style={{flex:1}} onClick={()=>setMonthlyHistoryStudent(null)}>Close</button>
                        </div>
                      </div>
                    </div>
                  );
                })()}
              </>
            )}
          </>
        );
      })()}

      {showPayment && payStudent && (
        <PaymentModal
          student={payStudent}
          onClose={() => { setShowPayment(false); setPayStudent(null); }}
          onSave={async () => {
            refreshPending();
            fetchMonthlyData();
            try {
              const allRes = await reportsAPI.getAllStudents(year);
              const freshAll = allRes.data.data || [];
              setAllStudents(freshAll);
              const fresh = freshAll.find((r) => r.student_id === payStudent.student_id);
              if (fresh) setPayStudent(fresh);
            } catch { refreshAllStudents(); }
          }}
        />
      )}

      {/* ΓöÇΓöÇ Voice Reminder generating overlay ΓöÇΓöÇ */}
      {voiceLoading && createPortal(
        <div className="modal-overlay">
          <div className="modal-box" style={{ textAlign: 'center', padding: '2rem' }}>
            <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>≡ƒÄÖ∩╕Å</div>
            <h3 style={{ marginBottom: '.5rem' }}>α▒çα░╡α░┐α░╕α▒ì α░«α▒åα░╕α▒çα░£α▒ì α░ñα░»α░╛α░░α░╡α▒üα░ñα▒üα░éα░ªα░┐...</h3>
            <p style={{ color: '#666', fontSize: '.9rem' }}>Azure Neural TTS α░ªα▒ìα░╡α░╛α░░α░╛ α░ñα▒åα░▓α▒üα░ùα▒ü α░╡α░╛α░»α░┐α░╕α▒ì α░ñα░»α░╛α░░α▒ü α░àα░╡α▒üα░ñα▒üα░éα░ªα░┐...</p>
          </div>
        </div>,
        document.body
      )}

      {/* ΓöÇΓöÇ Voice error popup ΓöÇΓöÇ */}
      {voiceError && createPortal(
        <div className="modal-overlay" onClick={() => setVoiceError(null)}>
          <div className="modal-box" style={{ textAlign: 'center', padding: '2rem' }}>
            <div style={{ fontSize: '2rem', marginBottom: '1rem' }}>ΓÜá∩╕Å</div>
            <h3>α░╡α░╛α░»α░┐α░╕α▒ì α░ñα░»α░╛α░░α▒ü α░╡α░┐α░½α░▓α░«α▒êα░éα░ªα░┐</h3>
            <p style={{ color: '#dc2626', margin: '1rem 0' }}>{voiceError}</p>
            {voiceError.includes('SPEECH_KEY') && (
              <p style={{ color: '#666', fontSize: '.85rem' }}>
                Azure Key Vault α░▓α▒ï <strong>SPEECH-KEY</strong> α░«α░░α░┐α░»α▒ü <strong>SPEECH-REGION</strong> α░╕α▒åα░ƒα▒ì α░Üα▒çα░»α░éα░íα░┐.
              </p>
            )}
            <button className="btn btn-secondary" onClick={() => setVoiceError(null)}>Close</button>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}
